import React, {useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {ChatProvider, FloatingChat} from '@sparkle/chat-widget'

import storage from 'utils/storage'

import {getChatEnv} from './chatEnv'
import {CHAT_MAX_UPLOAD_BYTES, CHAT_UPLOAD_ACCEPT, uploadChatAttachment} from './chatUploads'

const resolveDisplayName = (user) => {
  if (!user) return undefined
  const first = user.firstname || user.firstName
  const last = user.lastname || user.lastName
  const names = [first, last].filter((value) => !!value)
  if (names.length) return names.join(' ')
  return user.email || user.username || user.phone || user.mobile || undefined
}

const ChatBoundary = ({children}) => {
  const {pathname} = useLocation()
  const [displayName, setDisplayName] = useState()
  const [accountRoomId, setAccountRoomId] = useState()

  const {enabled: chatEnabled, endpoint, tenantId, storageKey} = getChatEnv()

  useEffect(() => {
    if (!chatEnabled) return
    if (typeof window === 'undefined') return

    let mounted = true
    const run = async () => {
      try {
        const rawUser = await storage.getUser()
        if (!mounted) return
        if (!rawUser) {
          setDisplayName(undefined)
          setAccountRoomId(undefined)
          return
        }

        const parsed = typeof rawUser === 'string' ? JSON.parse(rawUser) : rawUser
        setDisplayName(resolveDisplayName(parsed))

        const resolvedRoomKey =
          parsed?._id ||
          parsed?.id ||
          parsed?.uid ||
          parsed?.userId ||
          parsed?.email ||
          parsed?.username ||
          parsed?.phone ||
          parsed?.mobile ||
          undefined
        setAccountRoomId(resolvedRoomKey ? `account-${String(resolvedRoomKey)}` : undefined)
      } catch (error) {
        console.warn('ChatBoundary: unable to resolve identity', error)
        setDisplayName(undefined)
        setAccountRoomId(undefined)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [chatEnabled, pathname])

  const getJwt = useCallback(async () => {
    try {
      const token = storage.getToken()
      return token || undefined
    } catch (error) {
      console.warn('ChatBoundary: unable to resolve token', error)
      return undefined
    }
  }, [])

  if (!chatEnabled) return <>{children}</>

  const isSupportConsole = pathname?.startsWith('/support/console')
  if (isSupportConsole) return <>{children}</>

  return (
    <ChatProvider
      tenantId={tenantId}
      endpoint={endpoint}
      roomId={accountRoomId}
      authStrategy="auto"
      getJwt={getJwt}
      storageKey={storageKey}
      displayName={displayName}
      connectBehavior="open"
      idleDisconnectMs={120000}
      uploadFile={uploadChatAttachment}
      uploadAccept={CHAT_UPLOAD_ACCEPT}
      maxUploadBytes={CHAT_MAX_UPLOAD_BYTES}
    >
      {children}
      <FloatingChat projectName="Sparkle Timekeeping Support" hideLauncher />
    </ChatProvider>
  )
}

export default ChatBoundary
